import PropTypes from "prop-types";
import {
  ArrowLeft,
  ArrowRight,
  Banknote,
  BadgeCheck,
  FileCheck2,
  Mail,
  MailCheck,
  ShieldHalf
} from "lucide-react";

import nookPayLogo from "@src/assets/Favicon.png";
import useLogin from "./use-login";
import InputField from "@src/@core/components/fields/input-field";
import { CLIENT_TYPE_CARDS } from "./misc/constant";

const FEATURES = [
  {
    icon : BadgeCheck,
    title: "KYC-gated payouts",
    desc : "Verified publishers only. Held funds surface instantly."
  },
  {
    icon : FileCheck2,
    title: "Auto debit notes",
    desc : "Close every cycle in a click with itemised, audit-ready debit notes."
  },
  {
    icon : Banknote,
    title: "UTR-backed disbursements",
    desc : "Every payout carries a reference the brand can trace."
  }
];

const Login = () => {
  const { onSubmit, loading, methods, step, clientType, selectClientType, goBack } =
    useLogin();

  const email = methods.getValues("email");

  return (
    <div className="relative min-h-screen overflow-hidden bg-navy text-white">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-accent/25 blur-3xl" />
        <div className="absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-indigo-500/20 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage:
              "radial-gradient(ellipse at center, black 40%, transparent 75%)"
          }}
        />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col px-6 py-8 lg:flex-row lg:items-center lg:gap-16 lg:py-12">
        {/* Left — brand story */}
        <div className="flex-1 pb-10 lg:pb-0">
          <div className="mb-8 flex items-center gap-2.5">
            <img
              src={nookPayLogo}
              alt="NookPay"
              className="h-10 w-10 rounded-xl object-cover shadow-lg shadow-accent/30"
            />
            <div className="text-lg font-semibold tracking-tight">NookPay</div>
            <span className="ml-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-widest text-white/70">
              Payouts
            </span>
          </div>

          <h1 className="text-3xl font-semibold leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
            Publisher payouts,
            <br />
            <span className="text-accent">without the compliance drag.</span>
          </h1>
          <p className="mt-4 max-w-md text-sm leading-6 text-white/70 sm:text-base">
            One workspace to onboard brands, verify publisher KYC, raise debit
            notes, and disburse payouts — with a full audit trail on every
            rupee.
          </p>

          <div className="mt-8 grid max-w-md gap-3">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur"
              >
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent/15 text-accent">
                  <feature.icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-medium">{feature.title}</div>
                  <div className="text-xs text-white/60">{feature.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-2 text-xs text-white/60">
            <ShieldHalf className="h-3.5 w-3.5" />
            SOC-2 aligned · GST compliant · Ind-AS ready
          </div>
        </div>

        {/* Right — auth card */}
        <div className="w-full lg:max-w-md">
          <div className="rounded-2xl border border-white/10 bg-white p-6 text-gray-900 shadow-2xl shadow-black/40 sm:p-7">
            {step === "clientType" && (
              <>
                <div className="mb-1 text-xl font-semibold tracking-tight">
                  Welcome back
                </div>
                <p className="mb-5 text-sm text-muted">Sign in as</p>

                <div className="grid grid-cols-2 gap-3">
                  {CLIENT_TYPE_CARDS.map((card) => (
                    <button
                      key={card.type}
                      type="button"
                      onClick={() => selectClientType(card.type)}
                      className="group rounded-xl border border-gray-200 p-4 text-center transition-colors hover:border-primary hover:bg-primary/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      <card.icon className="mx-auto h-5 w-5 text-gray-700 transition-colors group-hover:text-primary" />
                      <div className="mt-2.5 text-base font-medium">
                        {card.label}
                      </div>
                      <div className="mt-1 text-xs text-muted">{card.desc}</div>
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => selectClientType("ops")}
                  className="mt-5 text-xs text-muted transition-colors hover:text-accent hover:underline"
                >
                  Nook Ops? Sign in with your work email
                </button>
              </>
            )}

            {step === "email" && (
              <>
                <button
                  type="button"
                  onClick={goBack}
                  className="mb-4 inline-flex items-center gap-1 text-xs font-medium text-muted transition-colors hover:text-accent"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </button>

                <div className="mb-1 text-xl font-semibold tracking-tight">
                  Sign in as {clientType?.label}
                </div>
                <p className="mb-5 text-sm text-muted">
                  Sign in with your work email to continue.
                </p>

                <form className="space-y-4" onSubmit={onSubmit}>
                  <InputField
                    control={methods.control}
                    name="email"
                    label="Work email"
                    placeholder="you@company.com"
                    prefix={{ icon: Mail }}
                    errors={methods.formState.errors}
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    ) : (
                      <>
                        Sign in
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                  <p className="pt-0.5 text-xs text-muted">
                    We&apos;ll email you a secure link to sign in — no password
                    needed.
                  </p>
                </form>
              </>
            )}

            {step === "sent" && (
              <div className="text-center">
                <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
                  <MailCheck size={22} />
                </span>
                <div className="text-xl font-semibold tracking-tight">
                  Check your inbox
                </div>
                <p className="mt-2 text-sm text-muted">
                  We&apos;ve sent a sign-in link to{" "}
                  <span className="font-medium text-gray-700">{email}</span>.
                  Click the link to continue.
                </p>
                <button
                  type="button"
                  onClick={goBack}
                  className="mt-6 text-sm font-medium text-accent hover:underline"
                >
                  Use a different email
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 text-center text-[11px] text-white/45">
            By continuing you agree to NookPay&apos;s Terms &amp; Privacy
            Notice.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
Login.propTypes = {
  greetingText   : PropTypes.string,
  greetingSubText: PropTypes.string,
  loginText      : PropTypes.string
};
